import { useCallback, useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Dumbbell } from "lucide-react";
import ConfirmDelete from "@/components/ConfirmDelete";
import ImageUpload from "@/components/ImageUpload";
import Thumb from "@/components/Thumb";

const CATEGORIES = ["Cardio", "Charge Libre", "Guidée", "Accessoire"];
const ETATS = ["Excellent", "Bon", "En Maintenance", "Hors Service"];
const EMPTY = { nom: "", categorie: "Cardio", quantite: 1, etat: "Bon", image_path: null };

const etatStyle = (e) =>
  e === "Excellent" ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
  : e === "Bon" ? "border-blue-500/40 text-blue-600 dark:text-blue-400"
  : e === "En Maintenance" ? "border-amber-500/40 text-amber-600 dark:text-amber-400"
  : "border-red-500/40 text-red-600 dark:text-red-400";

export default function Equipements() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [filtre, setFiltre] = useState("tous");
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => api.get("/equipements").then((r) => setItems(r.data)).catch(() => toast.error("Erreur de chargement")), []);
  useEffect(() => { refresh(); }, [refresh]);

  const openForm = (eq) => {
    setEditing(eq || null);
    setForm(eq ? { nom: eq.nom, categorie: eq.categorie, quantite: eq.quantite, etat: eq.etat, image_path: eq.image_path || null } : EMPTY);
    setDialog(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, quantite: Number(form.quantite) };
      if (editing) {
        await api.put(`/equipements/${editing.id}`, payload);
        toast.success("Équipement modifié");
      } else {
        await api.post("/equipements", payload);
        toast.success("Équipement ajouté");
      }
      setDialog(false);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/equipements/${toDelete.id}`);
      toast.success("Équipement supprimé");
      setToDelete(null);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!items) return <div className="text-muted-foreground text-sm animate-pulse" data-testid="equipements-loading">Chargement…</div>;

  const visibles = filtre === "tous" ? items : items.filter((i) => i.etat === filtre);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="equipements-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">Équipements</h1>
          <p className="text-sm text-muted-foreground mt-1">Inventaire du matériel de la salle</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filtre} onValueChange={setFiltre}>
            <SelectTrigger data-testid="equipement-filter-select" className="w-48 bg-card border-border text-foreground">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border text-foreground">
              <SelectItem value="tous">Tous les états</SelectItem>
              {ETATS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
          {can("equipements_gerer") && (
          <Button data-testid="add-equipement-button" onClick={() => openForm(null)} className="bg-emerald-500 hover:bg-emerald-600 text-foreground">
            <Plus className="w-4 h-4 mr-2" /> Ajouter
          </Button>
          )}
        </div>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="text-muted-foreground">Nom</TableHead>
                <TableHead className="text-muted-foreground">Catégorie</TableHead>
                <TableHead className="text-muted-foreground">Quantité</TableHead>
                <TableHead className="text-muted-foreground">État</TableHead>
                <TableHead className="text-muted-foreground text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibles.length === 0 && (
                <TableRow className="border-border">
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8" data-testid="equipements-empty">Aucun équipement</TableCell>
                </TableRow>
              )}
              {visibles.map((eq) => (
                <TableRow key={eq.id} className="border-border" data-testid={`equipement-row-${eq.id}`}>
                  <TableCell className="font-medium text-foreground">
                    <div className="flex items-center gap-3">
                      <Thumb path={eq.image_path} alt={eq.nom} icon={Dumbbell} testid={`equipement-photo-${eq.id}`} />
                      {eq.nom}
                    </div>
                  </TableCell>
                  <TableCell className="text-foreground/80">{eq.categorie}</TableCell>
                  <TableCell className="text-foreground/80">{eq.quantite}</TableCell>
                  <TableCell><Badge variant="outline" className={etatStyle(eq.etat)}>{eq.etat}</Badge></TableCell>
                  <TableCell className="text-right">
                    {can("equipements_gerer") && (
                    <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-emerald-600 dark:hover:text-emerald-400" data-testid={`edit-equipement-${eq.id}`} onClick={() => openForm(eq)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    )}
                    {can("equipements_gerer") && (
                      <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400" data-testid={`delete-equipement-${eq.id}`} onClick={() => setToDelete(eq)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle data-testid="equipement-dialog-title">{editing ? "Modifier l'équipement" : "Ajouter un équipement"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <ImageUpload value={form.image_path} onChange={(v) => setForm({ ...form, image_path: v })} testid="equipement-image-upload" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label className="text-foreground/80">Nom</Label>
              <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="equipement-nom-input" className="bg-background border-border text-foreground" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Catégorie</Label>
              <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                <SelectTrigger data-testid="equipement-categorie-select" className="bg-background border-border text-foreground"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Quantité</Label>
              <Input type="number" min="0" value={form.quantite} onChange={(e) => setForm({ ...form, quantite: e.target.value })} data-testid="equipement-quantite-input" className="bg-background border-border text-foreground" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">État</Label>
              <Select value={form.etat} onValueChange={(v) => setForm({ ...form, etat: v })}>
                <SelectTrigger data-testid="equipement-etat-select" className="bg-background border-border text-foreground"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  {ETATS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)} className="border-border text-foreground/80">Annuler</Button>
            <Button onClick={save} disabled={busy || !form.nom} data-testid="save-equipement-button" className="bg-emerald-500 hover:bg-emerald-600 text-foreground">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDelete open={!!toDelete} onOpenChange={() => setToDelete(null)} onConfirm={remove} title={toDelete?.nom} busy={busy} />
    </div>
  );
}
